import type { ReactElement } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ModalProps } from 'apps/core/ui/modal/modal';
import Modal from 'apps/core/ui/modal/modal';

type ModalState = {
  isOpen: boolean;
  isCustomModal: boolean;
  type: ModalProps['type'];
  title: ModalProps['title'];
  message: ModalProps['message'];
  confirm?: ModalProps['confirm'];
  cancel?: ModalProps['cancel'];
  renderActions?: ModalProps['renderActions'];
  customModalElement?: ReactElement;
};

interface TModalContext {
  showModal: (options: Omit<ModalState, 'isOpen' | 'customModalElement' | 'isCustomModal'>) => void;
  showCustomModal: (customModalElement: ModalState['customModalElement']) => void;
  closeModal: () => void;
}

const ModalContext = createContext<TModalContext>({
  showModal: () => {},
  showCustomModal: () => {},
  closeModal: () => {},
});

export const useModal = () => useContext(ModalContext);

type Props = { children: React.ReactElement };

export const ModalProvider = ({ children }: Props) => {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    isCustomModal: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showModal: TModalContext['showModal'] = useCallback((options) => {
    setModalState({ ...options, isCustomModal: false, isOpen: true });
  }, []);

  const showCustomModal: TModalContext['showCustomModal'] = useCallback((renderModal) => {
    setModalState((prev) => ({
      ...prev,
      isCustomModal: true,
      isOpen: true,
      customModalElement: renderModal,
    }));
  }, []);

  const closeModal: TModalContext['closeModal'] = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const providerValue: TModalContext = useMemo(
    () => ({ showModal, showCustomModal, closeModal }),
    [showModal, showCustomModal, closeModal],
  );

  return (
    <ModalContext.Provider value={providerValue}>
      {children}
      {modalState.isOpen &&
        (modalState.isCustomModal ? (
          modalState.customModalElement
        ) : (
          <Modal
            isOpen={modalState.isOpen}
            type={modalState.type}
            title={modalState.title}
            message={modalState.message}
            confirm={modalState.confirm}
            cancel={modalState.cancel}
            renderActions={modalState.renderActions}
            onClose={closeModal}
          />
        ))}
    </ModalContext.Provider>
  );
};
